import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { const u = await login(email, password); nav(u?.role === "driver" ? "/portal" : "/"); }
    catch (ex) { setErr(formatErr(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="hidden lg:flex sidebar-noise flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <img src="/ghost-logo.png" alt="Casper" className="h-11 w-11 rounded-md" />
          <div>
            <div className="font-display font-extrabold text-xl tracking-tight">GHOST TMS</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Transport Management</div>
          </div>
        </div>
        <div>
          <h1 className="font-display font-extrabold text-4xl leading-tight">Dispatch smarter.<br />Haul faster.</h1>
          <p className="text-white/50 mt-4 max-w-md">Requisitions, scheduling, driver hours-of-service, route matching and freight — in one friendly place.</p>
        </div>
        <div className="text-xs text-white/30 font-mono-data">v1.9 · Secure workspace</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src="/ghost-logo.png" alt="Casper" className="h-10 w-10 rounded-md bg-[#0A0A0A]" />
            <div className="font-display font-extrabold text-lg">GHOST TMS</div>
          </div>
          <div>
            <h2 className="font-display font-extrabold text-2xl">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to access the dashboard.</p>
          </div>
          {err && <div data-testid="login-error" className="text-sm rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2">{err}</div>}
          <div>
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-10" placeholder="you@company.com" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</Label>
            <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-10" placeholder="••••••••" />
          </div>
          <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full h-10 bg-[#0055FF] hover:bg-[#0044CC] gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </Button>
          <p className="text-xs text-muted-foreground text-center">Accounts are created by your administrator.</p>
        </form>
      </div>
    </div>
  );
}
